import React, { useState } from "react";
import { api } from "../lib/api";
import { MapPin, Mail, Instagram, Send, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const LICENSE_ITEMS = [
  "California Seller's Permit",
  "EIN Registered",
  "Family-Owned Business",
  "Located in Galt, California",
  "Future Hobby Shop Location",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent! We'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <div className="bg-[#262626] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">Say Hello</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tighter">Get in Touch</h1>
          <p className="mt-3 text-[#F9F6F0]/70 max-w-2xl">Questions about a card, want to sell or trade, or just want to talk collecting? We'd love to hear from you.</p>
          <p className="mt-4 text-[#F9F6F0]/60 max-w-2xl text-sm leading-relaxed">
            Hometown Era Cards is a California-based trading card retailer specializing in Pokémon, sports cards,
            graded collectibles, and sealed products. We are actively building inventory and distributor
            relationships while preparing for future retail expansion.
          </p>
          <p className="mt-3 text-[#D32F2F] text-sm font-semibold">
            Online retailer serving collectors throughout California and the United States.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2 space-y-8">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-[#D32F2F]/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#D32F2F]" /></div>
            <div>
              <h3 className="font-display font-bold text-lg text-[#262626]">Location</h3>
              <p className="text-[#262626]/70">Galt, California</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-[#D32F2F]/10 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#D32F2F]" /></div>
            <div>
              <h3 className="font-display font-bold text-lg text-[#262626]">Email</h3>
              <a href="mailto:sales@hometowneracards.com" className="text-[#D32F2F] font-semibold hover:underline">sales@hometowneracards.com</a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-[#D32F2F]/10 flex items-center justify-center shrink-0"><Instagram className="w-5 h-5 text-[#D32F2F]" /></div>
            <div>
              <h3 className="font-display font-bold text-lg text-[#262626]">Instagram</h3>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="text-[#D32F2F] font-semibold hover:underline">Follow our family shop</a>
            </div>
          </div>
        </div>

        <div className="md:col-span-3 bg-white rounded-lg border border-[#262626]/10 p-6 md:p-8">
          {sent ? (
            <div className="text-center py-12" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-[#D32F2F]/10 flex items-center justify-center mx-auto"><Send className="w-6 h-6 text-[#D32F2F]" /></div>
              <h3 className="mt-5 font-display font-bold text-2xl text-[#262626]">Thanks for reaching out!</h3>
              <p className="mt-2 text-[#262626]/70">We've received your message and will reply soon.</p>
              <button onClick={() => setSent(false)} className="mt-6 text-[#D32F2F] font-bold">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-bold text-[#262626] mb-2">Name</label>
                  <input required value={form.name} onChange={update("name")} data-testid="contact-name" className="w-full rounded-md border border-[#262626]/20 px-4 py-3 bg-[#F9F6F0] focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#262626] mb-2">Email</label>
                  <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email" className="w-full rounded-md border border-[#262626]/20 px-4 py-3 bg-[#F9F6F0] focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" placeholder="you@email.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#262626] mb-2">Subject</label>
                <input value={form.subject} onChange={update("subject")} data-testid="contact-subject" className="w-full rounded-md border border-[#262626]/20 px-4 py-3 bg-[#F9F6F0] focus:ring-2 focus:ring-[#D32F2F] focus:outline-none" placeholder="How can we help?" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#262626] mb-2">Message</label>
                <textarea required rows={5} value={form.message} onChange={update("message")} data-testid="contact-message" className="w-full rounded-md border border-[#262626]/20 px-4 py-3 bg-[#F9F6F0] focus:ring-2 focus:ring-[#D32F2F] focus:outline-none resize-none" placeholder="Tell us what you're looking for…" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#D32F2F] text-white font-bold px-8 py-3.5 rounded-md hover:bg-[#B71C1C] transition-colors disabled:opacity-60">
                {loading ? "Sending…" : <>Send Message <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Licensed California Retailer */}
      <section className="bg-white border-t border-[#262626]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">
              <BadgeCheck className="w-4 h-4" /> Trust & Transparency
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#262626]">Licensed California Retailer</h2>
            <p className="mt-4 text-[#262626]/70 leading-relaxed max-w-md">
              A registered, family-owned business built on trust — proudly operating within California with plans to grow into a local hobby shop.
            </p>
          </div>
          <ul className="space-y-3" data-testid="license-list">
            {LICENSE_ITEMS.map((item) => (
              <li key={item} className="flex items-center gap-3 bg-[#F9F6F0] border border-[#262626]/10 rounded-lg px-5 py-4">
                <BadgeCheck className="w-5 h-5 text-[#D32F2F] shrink-0" />
                <span className="font-semibold text-[#262626]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
